import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {useAuth0} from "../auth/SSO";
import {useNavBarContext} from "../navigation-bar/NavBar";
import Loading from "../common/Loading";
import PipeControl from "./PipeControl";
import PipeReadBack from "./PipeReadBack";
import UseHubDashboard from "./hooks/UseHubDashboard";
import HubHeader from "./HubHeader";
import HubHeaderMenu from "./HubHeaderMenu";
import HubSettingsDrawer from "../hub-settings/HubSettingsDrawer";

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        padding: theme.spacing(2),
    },
}));

function ControlPanel({pipes, handleSettingsChanged, handleSettingsApply, handleActionsApply}) {
    if (!pipes.length) return null;

    return (
        <Grid item xs={12} lg={3}
              container
              spacing={1}
              direction={"row"}
              justify={"center"}
        >
            {pipes.map(pipe => (
                <Grid key={`pipe-control-${pipe.id}`} item xs={12}>
                    <PipeControl pipe={pipe}
                                 handleSettingsChanged={handleSettingsChanged}
                                 handleSettingsApply={handleSettingsApply}
                                 handleActionsApply={handleActionsApply}
                    />
                </Grid>
            ))}
        </Grid>
    );
}

export default function HubDashboard({hubId}) {
    const classes = useStyles();

    const {loading, user} = useAuth0();
    const {handleTitleUpdate} = useNavBarContext();

    const [hubConfigurationOpen, setHubConfigurationOpen] = useState(false);

    if (loading || !user) {
        return <Loading/>;
    }

    const {
        hubLoading,
        buildLoading,
        pipesLoading,
        hub,
        build,
        pipes,
        buildLaunching,
        handleReload,
        handleBuildLaunch,
        handleBuildStop,
        handleSettingsChanged,
        handleSettingsApply,
        handleActionsApply
    } = UseHubDashboard({hubId});

    if (hubLoading || buildLoading || pipesLoading || !hub) {
        handleTitleUpdate("Loading...");
        return <Loading/>;
    }

    function handleHubConfigurationOpenChanged(value) {
        setHubConfigurationOpen(value);

        if (!value) handleReload();
    }

    const {title} = hub;

    handleTitleUpdate(title);

    const enabledPipes = pipes.filter(pipe => pipe.status === "ENABLED");

    const controlPipes = enabledPipes.map(pipe => {
        switch (pipe.vendor) {
            case "SPELLMAN_ICA":
                return Object.assign(pipe, {order: 1});
            case "SORENSEN_XG":
                return Object.assign(pipe, {order: 2});
            case "PEAK_COM_ARU2856C":
                return Object.assign(pipe, {order: 3});
            default:
                return null
        }
    }).filter(thing => thing).sort((a, b) => a.order - b.order);

    const readBackPipes = enabledPipes.map(pipe => {
        switch (pipe.vendor) {
            case "GAMMA_VACUUM_SPC_E":
                return Object.assign(pipe, {order: 1});
            case "PFEIFFER_VACUUM_TPG_261":
                return Object.assign(pipe, {order: 1});
            case "SORENSEN_XG":
                return Object.assign(pipe, {order: 2});
            case "SCANDINOVA_M1":
                return Object.assign(pipe, {order: 2});
            case "SPELLMAN_ICA":
                return Object.assign(pipe, {order: 3});
            default:
                return Object.assign(pipe, {order: Number.MAX_VALUE});
        }
    }).filter(thing => thing).sort((a, b) => a.order - b.order);

    const fullWidth = controlPipes.length === 0;

    return (
        <div className={classes.root}>
            <Grid container direction={"row"} alignItems={"baseline"} spacing={1}>
                <Grid item xs={12}>
                    <HubHeader hubId={hubId}
                               build={build}
                               loading={buildLaunching || handleBuildStop}
                               onLaunch={handleBuildLaunch}
                               onStop={handleBuildStop}
                    >
                        <HubHeaderMenu onHubConfigurationClick={() => handleHubConfigurationOpenChanged(true)}/>
                    </HubHeader>
                </Grid>
                <ControlPanel pipes={controlPipes}
                              handleSettingsChanged={handleSettingsChanged}
                              handleSettingsApply={handleSettingsApply}
                              handleActionsApply={handleActionsApply}/>
                <Grid item xs={12} lg={fullWidth ? 12 : 9}
                      container
                      spacing={1}
                      direction={"row"}
                      alignItems={"stretch"}
                >
                    {readBackPipes.map(pipe => (
                        <Grid key={`pipe-read-back-${pipe.id}`} item xs={12} lg={6}>
                            <PipeReadBack hub={hub} pipe={pipe}/>
                        </Grid>
                    ))}
                </Grid>
            </Grid>
            <HubSettingsDrawer hubId={hubId}
                               width={600}
                               open={hubConfigurationOpen}
                               onClose={() => handleHubConfigurationOpenChanged(false)}
            />
        </div>
    );
}